// src/utils/mockDataExtended.ts — Tier 2 mock data: Employees, Timesheets, Equipment, Resource Matrix

// ─────────────────────────────────────────────────────────────────
// PAY RATES (QLD EBA 2026 approximate)
// ─────────────────────────────────────────────────────────────────
// Phase 1 — Employment type additions
export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CASUAL' | 'SUBCONTRACTOR' | 'APPRENTICE' | 'HOST_APPRENTICE'

export const EMPLOYMENT_TYPE_META: Record<string, { label: string; bg: string; text: string }> = {
  FULL_TIME:       { label: 'Full time',       bg: 'bg-green-100',  text: 'text-green-700' },
  PART_TIME:       { label: 'Part time',        bg: 'bg-blue-100',   text: 'text-blue-700'  },
  CASUAL:          { label: 'Casual',           bg: 'bg-amber-100',  text: 'text-amber-700' },
  SUBCONTRACTOR:   { label: 'Subcontractor',    bg: 'bg-purple-100', text: 'text-purple-700'},
  APPRENTICE:      { label: 'Apprentice',       bg: 'bg-teal-100',   text: 'text-teal-700'  },
  HOST_APPRENTICE: { label: 'Host apprentice',  bg: 'bg-orange-100', text: 'text-orange-700'},
}

/** Calculate the current year of an apprenticeship (1–4) from commencement date */
export function calcApprenticeYear(commencementDate: string): 1 | 2 | 3 | 4 {
  const years = (Date.now() - new Date(commencementDate + 'T00:00:00').getTime())
                / (365.25 * 24 * 3600 * 1000)
  return Math.min(Math.max(Math.floor(years) + 1, 1), 4) as 1 | 2 | 3 | 4
}

/** Check if an employee was ≥21 at apprenticeship commencement */
export function calcIsAdultApprentice(dateOfBirth: string, commencementDate: string): boolean {
  const ageAtCommencement =
    (new Date(commencementDate + 'T00:00:00').getTime() - new Date(dateOfBirth + 'T00:00:00').getTime())
    / (365.25 * 24 * 3600 * 1000)
  return ageAtCommencement >= 21
}

export const PAY_RATES: Record<string, number> = {
  ELECTRICAL_A_GRADE:       43.00,
  ELECTRICAL_APPRENTICE_1:  20.50,
  ELECTRICAL_APPRENTICE_2:  24.00,
  ELECTRICAL_APPRENTICE_3:  29.00,
  ELECTRICAL_APPRENTICE_4:  34.50,
  TRAFFIC_CONTROLLER:       35.50,
  CIVIL_LABOURER:           32.00,
  SITE_MANAGER:             58.00,
  LEADING_HAND_ALLOWANCE:    2.50,
  PROJECT_MANAGER:          68.00,
  ADMINISTRATION:           35.00,
}

// ─────────────────────────────────────────────────────────────────
// EMPLOYEES
// ─────────────────────────────────────────────────────────────────
// EMPLOYEE DATA MODEL — Phase 1 additions
// New fields (all optional for backward compatibility):
//   Apprenticeship:  apprenticeCommencementDate, isAdultApprentice,
//                   trainingContractRef, trainingContractTerm,
//                   trainingContractExpiry, trainingProvider
//   GTO:            gtoArrangement (true = hosted apprentice),
//                   gtoName, gtoHostRate (daily $)
//   Workers comp:   workerCompActive, workerCompClaimRef,
//                   workerCompInjuryDate, workerCompInsurer
// ─────────────────────────────────────────────────────────────────
export const MOCK_EMPLOYEES = [
  {
    id: 'e1', employeeNumber: 'EMP-001',
    firstName: 'Tony', lastName: 'Rivera', fullName: 'Tony Rivera',
    email: 'tonyrivera@company.com.au', phone: '0412 345 678',
    dateOfBirth: '1985-03-14',
    address: '42 Smith St, Annerley QLD 4103',
    emergencyContact: 'Maria Rivera', emergencyPhone: '0411 222 333',
    trade: 'ELECTRICAL', employmentType: 'FULL_TIME',
    payClassification: 'ELECTRICAL_A_GRADE',
    systemTier: 'D_FIELD',
    currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    startDate: '2022-03-01',
    credentials: {
      electricalLicenceNumber: '88123QLD',
      electricalLicenceExpiry: '2027-06-30',
      firstAidExpiry: '2026-08-15',
      whsInductionExpiry: '2027-01-10',
      tcaExpiry: null,
      workingAtHeights: true,
      confinedSpaceEntry: true,
      hvAuthority: null,
    }
  },
  {
    id: 'e2', employeeNumber: 'EMP-002',
    firstName: 'Lisa', lastName: 'Grant', fullName: 'Lisa Grant',
    email: 'lisagrant@company.com.au', phone: '0423 456 789',
    dateOfBirth: '1990-07-22',
    address: '88 Park Rd, Woolloongabba QLD 4102',
    emergencyContact: 'James Grant', emergencyPhone: '0422 333 444',
    trade: 'TRAFFIC_CONTROL', employmentType: 'FULL_TIME',
    payClassification: 'TRAFFIC_CONTROLLER',
    systemTier: 'D_FIELD',
    currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    startDate: '2021-08-16',
    credentials: {
      electricalLicenceNumber: null,
      electricalLicenceExpiry: null,
      firstAidExpiry: '2026-07-08',
      whsInductionExpiry: '2026-07-01',
      tcaExpiry: '2026-07-08',
      workingAtHeights: false,
      confinedSpaceEntry: false,
      hvAuthority: null,
    }
  },
  {
    id: 'e3', employeeNumber: 'EMP-003',
    firstName: 'Sam', lastName: 'Nguyen', fullName: 'Sam Nguyen',
    email: 'samnguyen@company.com.au', phone: '0434 567 890',
    dateOfBirth: '1992-11-05',
    address: '15 Ipswich Rd, Moorooka QLD 4105',
    emergencyContact: 'Anh Nguyen', emergencyPhone: '0433 444 555',
    trade: 'ELECTRICAL', employmentType: 'CASUAL',
    payClassification: 'ELECTRICAL_A_GRADE',
    systemTier: 'D_FIELD',
    currentJobId: 'j4', currentJobNumber: 'TP-2026-004',
    startDate: '2023-01-09',
    credentials: {
      electricalLicenceNumber: '77456QLD',
      electricalLicenceExpiry: '2026-12-31',
      firstAidExpiry: '2027-03-20',
      whsInductionExpiry: '2027-06-15',
      tcaExpiry: null,
      workingAtHeights: true,
      confinedSpaceEntry: true,
      hvAuthority: 'HV-ENERGEX-2024-0042',
    }
  },
  {
    id: 'e4', employeeNumber: 'EMP-004',
    firstName: 'Ben', lastName: 'Walsh', fullName: 'Ben Walsh',
    email: 'benwalsh@company.com.au', phone: '0445 678 901',
    dateOfBirth: '1988-04-19',
    address: '3 Boundary Rd, Rocklea QLD 4106',
    emergencyContact: 'Claire Walsh', emergencyPhone: '0444 555 666',
    trade: 'CIVIL', employmentType: 'SUBCONTRACTOR',
    payClassification: 'CIVIL_LABOURER',
    systemTier: 'D_FIELD',
    currentJobId: 'j5', currentJobNumber: 'TP-2026-005',
    startDate: '2024-02-12',
    credentials: {
      electricalLicenceNumber: null,
      electricalLicenceExpiry: null,
      firstAidExpiry: '2027-01-30',
      whsInductionExpiry: '2027-01-30',
      tcaExpiry: null,
      workingAtHeights: false,
      confinedSpaceEntry: false,
      hvAuthority: null,
    }
  },
  {
    id: 'e5', employeeNumber: 'EMP-005',
    firstName: 'Karen', lastName: 'Patel', fullName: 'Karen Patel',
    email: 'karenpatel@company.com.au', phone: '0456 789 012',
    dateOfBirth: '1980-09-03',
    address: '72 Logan Rd, Woolloongabba QLD 4102',
    emergencyContact: 'Dev Patel', emergencyPhone: '0455 666 777',
    trade: 'ADMINISTRATION', employmentType: 'PART_TIME',
    payClassification: 'ADMINISTRATION',
    systemTier: 'C_PROCUREMENT',
    currentJobId: null, currentJobNumber: null,
    startDate: '2020-06-01',
    credentials: {
      electricalLicenceNumber: null,
      electricalLicenceExpiry: null,
      firstAidExpiry: '2027-04-12',
      whsInductionExpiry: '2027-04-12',
      tcaExpiry: null,
      workingAtHeights: false,
      confinedSpaceEntry: false,
      hvAuthority: null,
    }
  },
  {
    id: 'e6', employeeNumber: 'EMP-006',
    firstName: 'Dave', lastName: 'Fielding', fullName: 'Dave Fielding',
    email: 'davefielding@company.com.au', phone: '0467 890 123',
    dateOfBirth: '2002-01-28',
    address: '29 Boundary St, West End QLD 4101',
    emergencyContact: 'Sue Fielding', emergencyPhone: '0466 777 888',
    trade: 'ELECTRICAL', employmentType: 'FULL_TIME',
    payClassification: 'ELECTRICAL_APPRENTICE_3',
    systemTier: 'D_FIELD',
    currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    startDate: '2023-09-04',
    credentials: {
      electricalLicenceNumber: null,
      electricalLicenceExpiry: null,
      firstAidExpiry: '2026-09-15',
      whsInductionExpiry: '2027-09-03',
      tcaExpiry: null,
      workingAtHeights: false,
      confinedSpaceEntry: false,
      hvAuthority: null,
    }
  },
  {
    id: 'e7', employeeNumber: 'EMP-007',
    firstName: 'Mike', lastName: 'Torres', fullName: 'Mike Torres',
    email: 'miketorres@company.com.au', phone: '0478 901 234',
    dateOfBirth: '1983-06-11',
    address: '55 Montague Rd, West End QLD 4101',
    emergencyContact: 'Paula Torres', emergencyPhone: '0477 888 999',
    trade: 'ELECTRICAL', employmentType: 'FULL_TIME',
    payClassification: 'ELECTRICAL_A_GRADE',
    systemTier: 'D_FIELD',
    currentJobId: 'j2', currentJobNumber: 'TP-2026-002',
    startDate: '2019-05-20',
    credentials: {
      electricalLicenceNumber: '55789QLD',
      electricalLicenceExpiry: '2027-09-30',
      firstAidExpiry: '2027-05-20',
      whsInductionExpiry: '2027-05-20',
      tcaExpiry: '2027-02-14',
      workingAtHeights: true,
      confinedSpaceEntry: false,
      hvAuthority: null,
    }
  },
  {
    id: 'e8', employeeNumber: 'EMP-008',
    firstName: 'Sarah', lastName: 'Kim', fullName: 'Sarah Kim',
    email: 'sarahkim@company.com.au', phone: '0489 012 345',
    dateOfBirth: '1978-12-30',
    address: '8 Gladstone Rd, Highgate Hill QLD 4101',
    emergencyContact: 'Jin Kim', emergencyPhone: '0488 999 000',
    trade: 'ELECTRICAL', employmentType: 'FULL_TIME',
    payClassification: 'SITE_MANAGER',
    systemTier: 'B_PM',
    currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    startDate: '2017-02-06',
    credentials: {
      electricalLicenceNumber: '44321QLD',
      electricalLicenceExpiry: '2027-11-30',
      firstAidExpiry: '2026-07-15',
      whsInductionExpiry: '2027-02-05',
      tcaExpiry: null,
      workingAtHeights: true,
      confinedSpaceEntry: true,
      hvAuthority: null,
    }
  },
]

// Helper to get credential health
export function getCredentialHealth(employee: typeof MOCK_EMPLOYEES[0]) {
  const today = new Date()
  const thirtyDays = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
  const creds = employee.credentials
  const checks = [
    creds.electricalLicenceExpiry,
    creds.firstAidExpiry,
    creds.whsInductionExpiry,
    creds.tcaExpiry,
  ].filter(Boolean) as string[]

  if (checks.length === 0) return 'OK'
  const expired  = checks.some(d => new Date(d) < today)
  const expiring = checks.some(d => new Date(d) <= thirtyDays)
  if (expired)  return 'EXPIRED'
  if (expiring) return 'EXPIRING'
  return 'OK'
}

// ─────────────────────────────────────────────────────────────────
// TIMESHEETS — dynamic current week (HP5 fix — was hardcoded to 2026-06-15)
// ─────────────────────────────────────────────────────────────────
function _getMonday(): Date {
  const today = new Date()
  const day   = today.getDay()   // 0 = Sun, 1 = Mon … 6 = Sat
  const diff  = day === 0 ? -6 : 1 - day  // back to Monday
  const mon   = new Date(today)
  mon.setDate(today.getDate() + diff)
  mon.setHours(0, 0, 0, 0)
  return mon
}
function _isoDate(d: Date): string { return d.toISOString().slice(0, 10) }

const _weekStart = _getMonday()
export const WEEK_DATES: string[] = Array.from({ length: 7 }, (_, i) => {
  const d = new Date(_weekStart)
  d.setDate(_weekStart.getDate() + i)
  return _isoDate(d)
})

export const DAY_LABELS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']

export function calcHours(
  start: string,
  end: string,
  breakMins: number,
  dayIndex: number,
  shiftType: 'DAY' | 'NIGHT' | 'HALF' = 'DAY',
) {
  if (!start || !end) return { ordinary: 0, overtime: 0, doubleTime: 0, total: 0, nightLoading: 0 }
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  const raw = (eh * 60 + em - sh * 60 - sm - breakMins) / 60
  if (raw <= 0) return { ordinary: 0, overtime: 0, doubleTime: 0, total: raw, nightLoading: 0 }
  if (dayIndex === 0) return { ordinary: 0, overtime: 0, doubleTime: raw, total: raw, nightLoading: 0 }  // Sunday
  if (dayIndex === 5) return { ordinary: 0, overtime: Math.min(raw,2), doubleTime: Math.max(raw-2,0), total: raw, nightLoading: 0 } // Saturday
  const ordinary = Math.min(raw, 8)
  // Night loading applies to ordinary hours only — OT and DT carry their own penalty rates
  const nightLoading = shiftType === 'NIGHT' ? +(ordinary * (_nightShiftLoadingPct / 100)).toFixed(4) : 0
  return {
    ordinary,
    overtime:    Math.min(Math.max(raw - 8, 0), 2),
    doubleTime:  Math.max(raw - 10, 0),
    total:       raw,
    nightLoading,
  }
}

// Phase 1 — Pay period configuration (P8)
export type PayPeriod = 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY'
let _payPeriod: PayPeriod = 'WEEKLY'
export function getPayPeriod(): PayPeriod { return _payPeriod }
export function setPayPeriod(p: PayPeriod): void { _payPeriod = p }

export const COST_CODES = [
  { code: 'SITE-LABOUR',   label: 'General site labour' },
  { code: 'INSTALL-ELEC',  label: 'Electrical installation' },
  { code: 'INSTALL-CIVIL', label: 'Civil works' },
  { code: 'TC',            label: 'Traffic control' },
  { code: 'SUPERVISION',   label: 'Site supervision' },
  { code: 'TRAVEL',        label: 'Travel time' },
  { code: 'OVERHEAD',      label: 'Non-productive / overhead' },
]

export const MOCK_TIMESHEETS = [
  {
    id: 'ts1', employeeId: 'e1', employeeName: 'Tony Rivera',
    weekStarting: WEEK_DATES[0], status: 'SUBMITTED',
    submittedAt: '2026-06-20T17:45:00Z',
    approvedById: null, approvedAt: null,
    entries: [
      { id: 'te1',  date: '2026-06-15', dayIndex: 0, start: '07:00', end: '17:30', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'INSTALL-ELEC', activity: 'Conduit installation pit 1-6', fatigueOk: true },
      { id: 'te2',  date: '2026-06-16', dayIndex: 1, start: '07:00', end: '17:30', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'INSTALL-ELEC', activity: 'Cable pulling 6mm pwr cable', fatigueOk: true },
      { id: 'te3',  date: '2026-06-17', dayIndex: 2, start: '07:00', end: '18:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'INSTALL-ELEC', activity: 'Signal head connections', fatigueOk: true },
      { id: 'te4',  date: '2026-06-18', dayIndex: 3, start: '07:00', end: '17:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'INSTALL-ELEC', activity: 'Pit terminations and lids', fatigueOk: true },
      { id: 'te5',  date: '2026-06-19', dayIndex: 4, start: '07:00', end: '16:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'INSTALL-ELEC', activity: 'Phase testing and sign-off', fatigueOk: true },
      { id: 'te6',  date: '2026-06-20', dayIndex: 5, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
      { id: 'te7',  date: '2026-06-21', dayIndex: 6, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
    ]
  },
  {
    id: 'ts2', employeeId: 'e2', employeeName: 'Lisa Grant',
    weekStarting: WEEK_DATES[0], status: 'SUBMITTED',
    submittedAt: '2026-06-20T16:30:00Z',
    approvedById: null, approvedAt: null,
    entries: [
      { id: 'te8',  date: '2026-06-15', dayIndex: 0, start: '06:00', end: '14:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'TC', activity: 'Traffic management Kessels Rd', fatigueOk: true },
      { id: 'te9',  date: '2026-06-16', dayIndex: 1, start: '06:00', end: '14:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'TC', activity: 'Traffic management Kessels Rd', fatigueOk: true },
      { id: 'te10', date: '2026-06-17', dayIndex: 2, start: '06:00', end: '14:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'TC', activity: 'Traffic management Kessels Rd', fatigueOk: true },
      { id: 'te11', date: '2026-06-18', dayIndex: 3, start: '06:00', end: '14:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'TC', activity: 'Traffic management Kessels Rd', fatigueOk: true },
      { id: 'te12', date: '2026-06-19', dayIndex: 4, start: '06:00', end: '14:00', break: 30, jobId: 'j1', jobNumber: 'TP-2026-001', costCode: 'TC', activity: 'Traffic management Kessels Rd', fatigueOk: true },
      { id: 'te13', date: '2026-06-20', dayIndex: 5, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
      { id: 'te14', date: '2026-06-21', dayIndex: 6, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
    ]
  },
  {
    id: 'ts3', employeeId: 'e3', employeeName: 'Sam Nguyen',
    weekStarting: WEEK_DATES[0], status: 'DRAFT',
    submittedAt: null, approvedById: null, approvedAt: null,
    entries: [
      { id: 'te15', date: '2026-06-15', dayIndex: 0, start: '07:00', end: '17:30', break: 30, jobId: 'j4', jobNumber: 'TP-2026-004', costCode: 'INSTALL-ELEC', activity: 'HV cable installation — section 1', fatigueOk: true },
      { id: 'te16', date: '2026-06-16', dayIndex: 1, start: '07:00', end: '18:30', break: 30, jobId: 'j4', jobNumber: 'TP-2026-004', costCode: 'INSTALL-ELEC', activity: 'HV cable jointing — pit A', fatigueOk: true },
      { id: 'te17', date: '2026-06-17', dayIndex: 2, start: '07:00', end: '17:30', break: 30, jobId: 'j4', jobNumber: 'TP-2026-004', costCode: 'INSTALL-ELEC', activity: 'Terminations substation end', fatigueOk: true },
      { id: 'te18', date: '2026-06-18', dayIndex: 3, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
      { id: 'te19', date: '2026-06-19', dayIndex: 4, start: '07:00', end: '17:30', break: 30, jobId: 'j4', jobNumber: 'TP-2026-004', costCode: 'INSTALL-ELEC', activity: 'VLF cable testing', fatigueOk: true },
      { id: 'te20', date: '2026-06-20', dayIndex: 5, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
      { id: 'te21', date: '2026-06-21', dayIndex: 6, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
    ]
  },
  {
    id: 'ts4', employeeId: 'e7', employeeName: 'Mike Torres',
    weekStarting: WEEK_DATES[0], status: 'APPROVED',
    submittedAt: '2026-06-20T15:00:00Z',
    approvedById: 'u2', approvedAt: '2026-06-20T16:00:00Z',
    entries: [
      { id: 'te22', date: '2026-06-15', dayIndex: 0, start: '07:00', end: '17:00', break: 30, jobId: 'j2', jobNumber: 'TP-2026-002', costCode: 'SITE-LABOUR', activity: 'Site setup Pacific Mwy', fatigueOk: true },
      { id: 'te23', date: '2026-06-16', dayIndex: 1, start: '07:00', end: '17:00', break: 30, jobId: 'j2', jobNumber: 'TP-2026-002', costCode: 'INSTALL-ELEC', activity: 'Conduit installation', fatigueOk: true },
      { id: 'te24', date: '2026-06-17', dayIndex: 2, start: '07:00', end: '17:00', break: 30, jobId: 'j2', jobNumber: 'TP-2026-002', costCode: 'INSTALL-ELEC', activity: 'Cable pulling', fatigueOk: true },
      { id: 'te25', date: '2026-06-18', dayIndex: 3, start: '07:00', end: '17:00', break: 30, jobId: 'j2', jobNumber: 'TP-2026-002', costCode: 'INSTALL-ELEC', activity: 'Controller cabinet install', fatigueOk: true },
      { id: 'te26', date: '2026-06-19', dayIndex: 4, start: '07:00', end: '17:00', break: 30, jobId: 'j2', jobNumber: 'TP-2026-002', costCode: 'INSTALL-ELEC', activity: 'Loop detector installation', fatigueOk: true },
      { id: 'te27', date: '2026-06-20', dayIndex: 5, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
      { id: 'te28', date: '2026-06-21', dayIndex: 6, start: '', end: '', break: 0, jobId: null, jobNumber: null, costCode: '', activity: '', fatigueOk: true },
    ]
  },
]

// HP5 COMPLETE — make all timesheet entry dates relative to the live current week.
// WEEK_DATES[0] = this Monday, WEEK_DATES[1] = Tuesday, etc.
// dayIndex was already stored on each entry but never used to set the date.
MOCK_TIMESHEETS.forEach(ts => {
  ts.weekStarting = WEEK_DATES[0]
  ts.entries.forEach((e: any) => { e.date = WEEK_DATES[e.dayIndex] ?? e.date })
})

// ─────────────────────────────────────────────────────────────────
// EQUIPMENT REGISTER
// ─────────────────────────────────────────────────────────────────
export const MOCK_EQUIPMENT = [
  // VEHICLES
  {
    id: 'eq1', assetNumber: 'VEH-001', category: 'VEHICLE',
    name: 'Ford Ranger XL 2023', make: 'Ford', model: 'Ranger XL', year: 2023,
    registrationNumber: '1ABC 123', serialNumber: 'VIN-FORD-ABC123',
    status: 'ALLOCATED', currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    ownedOrLeased: 'OWNED', purchaseCost: 48000, currentValue: 40000,
    regoExpiry: '2026-11-30', ctpExpiry: '2026-11-30', insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-08-15', nextServiceKm: 75000, currentOdometer: 68400,
    calibrationDue: null,
    defects: [],
  },
  {
    id: 'eq2', assetNumber: 'VEH-002', category: 'VEHICLE',
    name: 'Toyota Hilux SR 2022', make: 'Toyota', model: 'Hilux SR', year: 2022,
    registrationNumber: '2XYZ 456', serialNumber: 'VIN-TOY-XYZ456',
    status: 'ALLOCATED', currentJobId: 'j2', currentJobNumber: 'TP-2026-002',
    ownedOrLeased: 'OWNED', purchaseCost: 52000, currentValue: 42000,
    regoExpiry: '2026-09-14', ctpExpiry: '2026-09-14', insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-07-20', nextServiceKm: 82000, currentOdometer: 79800,
    calibrationDue: null,
    defects: [],
  },
  {
    id: 'eq3', assetNumber: 'VEH-003', category: 'VEHICLE',
    name: 'Isuzu NPS 75-155 2021', make: 'Isuzu', model: 'NPS 75-155', year: 2021,
    registrationNumber: '3DEF 789', serialNumber: 'VIN-ISU-DEF789',
    status: 'IN_SERVICE', currentJobId: null, currentJobNumber: null,
    ownedOrLeased: 'OWNED', purchaseCost: 95000, currentValue: 72000,
    regoExpiry: '2026-07-14', ctpExpiry: '2026-07-14', insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-07-01', nextServiceKm: 120000, currentOdometer: 118500,
    calibrationDue: null,
    defects: [{ id: 'd1', severity: 'MINOR', description: 'Rear left tyre low pressure indicator', status: 'OPEN', reportedAt: '2026-06-15' }],
  },
  {
    id: 'eq4', assetNumber: 'VEH-004', category: 'VEHICLE',
    name: 'Isuzu D-Max 2024', make: 'Isuzu', model: 'D-Max', year: 2024,
    registrationNumber: '4GHI 012', serialNumber: 'VIN-ISU-GHI012',
    status: 'AVAILABLE', currentJobId: null, currentJobNumber: null,
    ownedOrLeased: 'OWNED', purchaseCost: 58000, currentValue: 54000,
    regoExpiry: '2027-03-31', ctpExpiry: '2027-03-31', insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-12-01', nextServiceKm: 15000, currentOdometer: 8200,
    calibrationDue: null,
    defects: [],
  },
  // PLANT
  {
    id: 'eq5', assetNumber: 'PLT-001', category: 'PLANT',
    name: 'Kubota KX016-4 Excavator', make: 'Kubota', model: 'KX016-4', year: 2020,
    registrationNumber: null, serialNumber: 'KUB-KX016-55421',
    status: 'ALLOCATED', currentJobId: 'j4', currentJobNumber: 'TP-2026-004',
    ownedOrLeased: 'OWNED', purchaseCost: 68000, currentValue: 48000,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-07-20', nextServiceKm: null, currentOdometer: null,
    calibrationDue: null,
    defects: [],
  },
  {
    id: 'eq6', assetNumber: 'PLT-002', category: 'PLANT',
    name: 'Snorkel A46JE EWP 12m', make: 'Snorkel', model: 'A46JE', year: 2019,
    registrationNumber: null, serialNumber: 'SNK-A46JE-18842',
    status: 'ALLOCATED', currentJobId: 'j5', currentJobNumber: 'TP-2026-005',
    ownedOrLeased: 'OWNED', purchaseCost: 42000, currentValue: 28000,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: '2027-02-14',
    nextServiceDate: '2026-08-30', nextServiceKm: null, currentOdometer: null,
    calibrationDue: null,
    defects: [],
  },
  {
    id: 'eq7', assetNumber: 'PLT-003', category: 'PLANT',
    name: 'Wacker WP1550AW Plate Compactor', make: 'Wacker', model: 'WP1550AW', year: 2021,
    registrationNumber: null, serialNumber: 'WAK-WP1550-33210',
    status: 'AVAILABLE', currentJobId: null, currentJobNumber: null,
    ownedOrLeased: 'OWNED', purchaseCost: 4800, currentValue: 3500,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: '2026-09-01', nextServiceKm: null, currentOdometer: null,
    calibrationDue: null,
    defects: [],
  },
  // TEST EQUIPMENT
  {
    id: 'eq8', assetNumber: 'TST-001', category: 'TEST_EQUIPMENT',
    name: 'Megger MIT430/2 Insulation Tester', make: 'Megger', model: 'MIT430/2', year: 2022,
    registrationNumber: null, serialNumber: 'MEG-MIT430-88123',
    status: 'AVAILABLE', currentJobId: null, currentJobNumber: null,
    ownedOrLeased: 'OWNED', purchaseCost: 1850, currentValue: 1400,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: null, nextServiceKm: null, currentOdometer: null,
    calibrationDue: '2026-08-01',
    defects: [],
  },
  {
    id: 'eq9', assetNumber: 'TST-002', category: 'TEST_EQUIPMENT',
    name: 'Fluke 376 FC Clamp Meter', make: 'Fluke', model: '376 FC', year: 2023,
    registrationNumber: null, serialNumber: 'FLK-376FC-44215',
    status: 'ALLOCATED', currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    ownedOrLeased: 'OWNED', purchaseCost: 780, currentValue: 650,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: null, nextServiceKm: null, currentOdometer: null,
    calibrationDue: '2026-12-15',
    defects: [],
  },
  {
    id: 'eq10', assetNumber: 'TST-003', category: 'TEST_EQUIPMENT',
    name: 'Radiodetection RD8100 Cable Locator', make: 'Radiodetection', model: 'RD8100', year: 2021,
    registrationNumber: null, serialNumber: 'RD-8100-77632',
    status: 'ALLOCATED', currentJobId: 'j4', currentJobNumber: 'TP-2026-004',
    ownedOrLeased: 'OWNED', purchaseCost: 6200, currentValue: 4500,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: null, nextServiceKm: null, currentOdometer: null,
    calibrationDue: '2026-07-08',
    defects: [],
  },
  // TRAFFIC CONTROL
  {
    id: 'eq11', assetNumber: 'TRF-001', category: 'TRAFFIC_CONTROL',
    name: 'Traffic Management Set — Full (signs, delineators, barriers)', make: 'Various', model: 'Full Set', year: 2022,
    registrationNumber: null, serialNumber: null,
    status: 'ALLOCATED', currentJobId: 'j1', currentJobNumber: 'TP-2026-001',
    ownedOrLeased: 'OWNED', purchaseCost: 12000, currentValue: 9500,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: '2026-09-01', nextServiceKm: null, currentOdometer: null,
    calibrationDue: null,
    defects: [],
  },
  {
    id: 'eq12', assetNumber: 'TRF-002', category: 'TRAFFIC_CONTROL',
    name: 'Portable VMS Sign (Solar)', make: 'Wanco', model: 'WVMS-3', year: 2023,
    registrationNumber: null, serialNumber: 'WAN-WVMS3-22018',
    status: 'AVAILABLE', currentJobId: null, currentJobNumber: null,
    ownedOrLeased: 'OWNED', purchaseCost: 14500, currentValue: 12000,
    regoExpiry: null, ctpExpiry: null, insuranceExpiry: null,
    nextServiceDate: null, nextServiceKm: null, currentOdometer: null,
    calibrationDue: null,
    defects: [],
  },
]

// ─────────────────────────────────────────────────────────────────
// RESOURCE MATRIX — week of 16 Jun 2026
// ─────────────────────────────────────────────────────────────────
// Each cell = which job (or null=available) per employee per day Mon-Fri
export const RESOURCE_MATRIX_CREW = [
  { employeeId: 'e1', name: 'Tony Rivera',   trade: 'Electrician',     week: ['j1','j1','j1','j1','j1', null, null] },
  { employeeId: 'e2', name: 'Lisa Grant',    trade: 'Traffic Control', week: ['j1','j1','j1','j1','j1', null, null] },
  { employeeId: 'e3', name: 'Sam Nguyen',    trade: 'Electrician (HV)',week: ['j4','j4','j4', null,'j4', null, null] },
  { employeeId: 'e4', name: 'Ben Walsh',     trade: 'Civil Labourer',  week: ['j5','j5','j5','j5','j5', null, null] },
  { employeeId: 'e6', name: 'Dave Fielding', trade: 'Apprentice Yr3',  week: ['j1','j1','j1','j1','j1', null, null] },
  { employeeId: 'e7', name: 'Mike Torres',   trade: 'Electrician',     week: ['j2','j2','j2','j2','j2', null, null] },
  { employeeId: 'e8', name: 'Sarah Kim',     trade: 'Site Manager',    week: ['j1','j1','j2','j1','j1', null, null] },
]

export const RESOURCE_MATRIX_PLANT = [
  { assetId: 'eq1', name: 'Ford Ranger VEH-001',       category: 'Vehicle',      week: ['j1','j1','j1','j1','j1', null, null] },
  { assetId: 'eq2', name: 'Toyota Hilux VEH-002',      category: 'Vehicle',      week: ['j2','j2','j2','j2','j2', null, null] },
  { assetId: 'eq3', name: 'Isuzu NPS VEH-003',         category: 'Vehicle',      week: ['SVC','SVC','SVC', null, null, null, null] },
  { assetId: 'eq5', name: 'Kubota Excavator PLT-001',  category: 'Plant',        week: ['j4','j4','j4','j4','j4', null, null] },
  { assetId: 'eq6', name: 'Snorkel EWP 12m PLT-002',  category: 'Plant',        week: ['j5','j5','j5','j5','j5', null, null] },
  { assetId: 'eq9', name: 'Fluke Clamp Meter TST-002', category: 'Test Equip',  week: ['j1','j1','j1','j1','j1', null, null] },
  { assetId: 'eq10',name: 'RD8100 Cable Locator TST-003',category:'Test Equip', week: ['j4','j4','j4','j4','j4', null, null] },
]

export const JOB_COLOUR: Record<string, { bg: string; text: string; label: string }> = {
  j1:  { bg: 'bg-blue-500',   text: 'text-white',     label: 'TP-2026-001' },
  j2:  { bg: 'bg-indigo-500', text: 'text-white',     label: 'TP-2026-002' },
  j3:  { bg: 'bg-teal-500',   text: 'text-white',     label: 'TP-2026-003' },
  j4:  { bg: 'bg-amber-500',  text: 'text-white',     label: 'TP-2026-004' },
  j5:  { bg: 'bg-purple-500', text: 'text-white',     label: 'TP-2026-005' },
  j6:  { bg: 'bg-pink-500',   text: 'text-white',     label: 'TP-2026-006' },
  SVC: { bg: 'bg-orange-400', text: 'text-white',     label: 'Service' },
  null:{ bg: 'bg-gray-100',   text: 'text-gray-400',  label: 'Available' },
}

// Compliance alert helpers
export function getComplianceAlerts(equipment: typeof MOCK_EQUIPMENT) {
  const today = new Date()
  const in30  = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
  const in60  = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000)
  const alerts: { id: string; assetNumber: string; name: string; type: string; dueDate: string; daysLeft: number; severity: 'HIGH'|'MEDIUM' }[] = []

  for (const eq of equipment) {
    const checks = [
      { type: 'Registration',     date: eq.regoExpiry,        threshold: in30 },
      { type: 'CTP Insurance',    date: eq.ctpExpiry,         threshold: in30 },
      { type: 'Insurance',        date: eq.insuranceExpiry,   threshold: in60 },
      { type: 'Scheduled Service',date: eq.nextServiceDate,   threshold: in30 },
      { type: 'Calibration',      date: eq.calibrationDue,    threshold: in30 },
    ]
    for (const c of checks) {
      if (!c.date) continue
      const due = new Date(c.date)
      if (due <= c.threshold) {
        const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
        alerts.push({ id: `${eq.id}-${c.type}`, assetNumber: eq.assetNumber, name: eq.name, type: c.type, dueDate: c.date, daysLeft, severity: daysLeft <= 14 ? 'HIGH' : 'MEDIUM' })
      }
    }
  }
  return alerts.sort((a, b) => a.daysLeft - b.daysLeft)
}

// ─────────────────────────────────────────────────────────────────
// NIGHT SHIFT LOADING — configurable from Settings > Pay & Rates
// Added pre-phase (night loading fix) — required by Settings.tsx and Timesheets.tsx
// Default: 15% per Building and Construction General On-site Award 2020
// ─────────────────────────────────────────────────────────────────
let _nightShiftLoadingPct: number = 15

export function setNightShiftLoading(pct: number): void {
  _nightShiftLoadingPct = Math.max(0, Math.min(100, pct))
}

export function getNightShiftLoading(): number {
  return _nightShiftLoadingPct
}

// ══════════════════════════════════════════════════════════════════
// PHASE 3 DATA LAYER ADDITIONS
// ══════════════════════════════════════════════════════════════════

// ─── I2: Equipment open-defect store ─────────────────────────────
// Seeded with one OPEN defect on eq2 (Kubota Excavator) for demo.
// PlantEquipment.tsx adds defects via addEquipmentDefect().
// Roster equipment grid reads via getOpenEquipmentDefects() to block
// allocation of any asset with an OPEN defect status.
export interface EquipmentDefectRecord {
  id:          string
  equipmentId: string
  assetNumber: string
  severity:    'CRITICAL' | 'MAJOR' | 'MINOR'
  description: string
  status:      'OPEN' | 'CLOSED' | 'MONITORING'
  reportedAt:  string
}

const _equipmentDefects: EquipmentDefectRecord[] = [
  {
    id: 'def001', equipmentId: 'eq2', assetNumber: 'PLT-001',
    severity: 'CRITICAL',
    description: 'Hydraulic leak on boom — do not operate until repaired',
    status: 'OPEN', reportedAt: '2026-07-08',
  },
]

export function getOpenEquipmentDefects(equipmentId?: string): EquipmentDefectRecord[] {
  const open = _equipmentDefects.filter(d => d.status === 'OPEN')
  return equipmentId ? open.filter(d => d.equipmentId === equipmentId) : open
}

export function addEquipmentDefect(defect: Omit<EquipmentDefectRecord,'id'>): void {
  _equipmentDefects.push({ ...defect, id: `def-${Date.now()}` })
}

export function closeEquipmentDefect(defectId: string): void {
  const d = _equipmentDefects.find(x => x.id === defectId)
  if (d) d.status = 'CLOSED'
}

// ─── E1: Site induction lookup ───────────────────────────────────
// Per-employee map of jobId → induction status.
// Roster AllocateModal reads this to gate allocation (E1).
// In production, reads from the Inductions module via API.
export type InductionStatus = 'INDUCTED' | 'NOT_INDUCTED' | 'EXPIRED'

const _siteInductions: Map<string, Map<string, InductionStatus>> = new Map([
  ['e1', new Map<string,InductionStatus>([['j1','INDUCTED'],['j2','INDUCTED'],['j4','INDUCTED']])],
  ['e2', new Map<string,InductionStatus>([['j1','INDUCTED'],['j2','INDUCTED']])],
  ['e3', new Map<string,InductionStatus>([['j1','INDUCTED'],['j4','INDUCTED']])],
  ['e4', new Map<string,InductionStatus>([['j4','INDUCTED'],['j5','INDUCTED']])],
  ['e5', new Map<string,InductionStatus>([])],
  ['e6', new Map<string,InductionStatus>([['j1','INDUCTED'],['j2','INDUCTED'],['j3','INDUCTED']])],
  ['e7', new Map<string,InductionStatus>([['j1','INDUCTED'],['j2','INDUCTED'],['j3','INDUCTED']])],
  ['e8', new Map<string,InductionStatus>([['j1','INDUCTED']])],
  ['e9', new Map<string,InductionStatus>([['j5','INDUCTED'],['j4','INDUCTED']])],
])

export function getEmployeeInductionStatus(employeeId: string, jobId: string): InductionStatus {
  return _siteInductions.get(employeeId)?.get(jobId) ?? 'NOT_INDUCTED'
}

export function recordSiteInduction(employeeId: string, jobId: string, status: InductionStatus = 'INDUCTED'): void {
  if (!_siteInductions.has(employeeId)) _siteInductions.set(employeeId, new Map())
  _siteInductions.get(employeeId)!.set(jobId, status)
}

// ─── I1 / F2: FM (fatigue management) status from allocations ────
// Calculates consecutive working days from allocation history.
// A full FM flag integration reads from Travel.tsx module state
// in production; this prototype derives FM status from allocations.
export function getEmployeeFMStatus(
  employeeId: string,
  allocations: Array<{ employeeId: string; date: string }>,
): 'RED' | 'AMBER' | 'CLEAR' {
  const days = allocations
    .filter(a => a.employeeId === employeeId)
    .map(a => a.date)
    .sort()
    .filter((d, i, arr) => arr.indexOf(d) === i)  // dedupe

  if (days.length === 0) return 'CLEAR'
  let maxConsec = 1, consec = 1
  for (let i = 1; i < days.length; i++) {
    const diff = (new Date(days[i]).getTime() - new Date(days[i-1]).getTime()) / 86400000
    consec = diff === 1 ? consec + 1 : 1
    maxConsec = Math.max(maxConsec, consec)
  }
  if (maxConsec >= 6) return 'RED'
  if (maxConsec >= 4) return 'AMBER'
  return 'CLEAR'
}

// ─── F1: Minimum rest period check (10h between shifts) ──────────
// Uses shift window assumptions:
//   DAY shift: 07:00–17:00 (10h window)
//   NIGHT shift: 21:00–05:00+1 (8h window, ends following morning)
// Returns 'BLOCK' if <10h rest, 'WARN' if 10-12h, 'OK' otherwise.
export function checkMinRestPeriod(
  employeeId: string,
  proposedDate: string,
  proposedShift: 'DAY' | 'NIGHT' | 'HALF',
  allocations: Array<{ employeeId: string; date: string; shiftType: string }>,
): { status: 'BLOCK' | 'WARN' | 'OK'; reason: string } {
  const empAllocs = allocations.filter(a => a.employeeId === employeeId)
  const proposedD = new Date(proposedDate + 'T00:00:00')

  // DAY shift: starts 07:00 on proposedDate
  // NIGHT shift: starts 21:00 on proposedDate, ends 05:00 next day
  const proposedStartH = proposedShift === 'NIGHT' ? 21 : 7
  const proposedStart  = new Date(proposedDate + 'T00:00:00').getTime() + proposedStartH * 3600000

  for (const a of empAllocs) {
    if (a.date === proposedDate && a.shiftType === proposedShift) continue  // same cell, skip

    const aDate = new Date(a.date + 'T00:00:00')
    const dayDiff = Math.round((proposedD.getTime() - aDate.getTime()) / 86400000)

    // Previous shift end time
    let prevEndMs: number | null = null
    if (a.shiftType === 'NIGHT') {
      // Night ends 05:00 NEXT calendar day
      prevEndMs = aDate.getTime() + 86400000 + 5 * 3600000
    } else if (a.shiftType === 'DAY') {
      // Day ends 17:00 same day
      prevEndMs = aDate.getTime() + 17 * 3600000
    }
    if (prevEndMs === null) continue

    // Only check adjacent dates (within 1 day either side)
    if (Math.abs(dayDiff) > 1) continue

    const restHours = (proposedStart - prevEndMs) / 3600000
    if (restHours < 0) continue   // proposed is before previous — different check
    if (restHours < 10) return { status: 'BLOCK', reason: `Only ${restHours.toFixed(1)}h rest between ${a.shiftType} shift on ${a.date} and proposed ${proposedShift} shift. Minimum 10h required (EBA clause).` }
    if (restHours < 12) return { status: 'WARN',  reason: `${restHours.toFixed(1)}h rest between shifts. Minimum 10h met but EBA recommends 12h. Proceed with PM approval.` }
  }
  return { status: 'OK', reason: '' }
}

// ─── AP6: Supervisor credential level for task types ─────────────
// Maps task keywords to the credential required of the supervisor.
export const SUPERVISOR_CREDENTIAL_REQUIREMENTS: Array<{
  taskKeywords: string[];
  requiredCredential: keyof typeof CREDENTIAL_LABELS;
  label: string;
}> = [
  { taskKeywords: ['hv','high voltage','energised','kv'],      requiredCredential: 'hvAuthority',         label: 'HV authority' },
  { taskKeywords: ['confined space','pit','vault','manhole'],  requiredCredential: 'confinedSpaceEntry',  label: 'Confined space entry' },
  { taskKeywords: ['height','elevated','ewp','ladder','roof'], requiredCredential: 'workingAtHeights',    label: 'Working at heights' },
]

export const CREDENTIAL_LABELS = {
  electricalLicenceNumber: 'Electrical licence',
  firstAidExpiry:          'First aid',
  whsInductionExpiry:      'WHS induction',
  tcaExpiry:               'Traffic control authority',
  workingAtHeights:        'Working at heights',
  confinedSpaceEntry:      'Confined space entry',
  hvAuthority:             'HV authority',
}

export function getExpiredCredentials(credentials: Record<string, any>): string[] {
  const expired: string[] = []
  const today = new Date().toISOString().slice(0,10)
  if (credentials.electricalLicenceExpiry && credentials.electricalLicenceExpiry < today)
    expired.push('Electrical licence (EXPIRED)')
  if (credentials.firstAidExpiry && credentials.firstAidExpiry < today)
    expired.push('First aid certificate (EXPIRED)')
  if (credentials.whsInductionExpiry && credentials.whsInductionExpiry < today)
    expired.push('WHS induction (EXPIRED)')
  if (credentials.tcaExpiry && credentials.tcaExpiry < today)
    expired.push('Traffic control authority (EXPIRED)')
  return expired
}

export function getExpiringCredentials(credentials: Record<string, any>, withinDays = 30): string[] {
  const expiring: string[] = []
  const threshold = new Date(Date.now() + withinDays * 86400000).toISOString().slice(0,10)
  const today     = new Date().toISOString().slice(0,10)
  if (credentials.electricalLicenceExpiry && credentials.electricalLicenceExpiry > today && credentials.electricalLicenceExpiry < threshold)
    expiring.push(`Electrical licence (expires ${credentials.electricalLicenceExpiry})`)
  if (credentials.firstAidExpiry && credentials.firstAidExpiry > today && credentials.firstAidExpiry < threshold)
    expiring.push(`First aid (expires ${credentials.firstAidExpiry})`)
  if (credentials.whsInductionExpiry && credentials.whsInductionExpiry > today && credentials.whsInductionExpiry < threshold)
    expiring.push(`WHS induction (expires ${credentials.whsInductionExpiry})`)
  if (credentials.tcaExpiry && credentials.tcaExpiry > today && credentials.tcaExpiry < threshold)
    expiring.push(`TCA (expires ${credentials.tcaExpiry})`)
  return expiring
}

// ══════════════════════════════════════════════════════════════════
// PHASE 4 DATA LAYER ADDITIONS
// ══════════════════════════════════════════════════════════════════

// ─── P3: Per-classification overtime thresholds ───────────────────
// Different pay classifications have different daily ordinary-time
// thresholds before overtime kicks in. These are Award-based defaults
// that can be overridden by EBA settings in the Settings PayRateStore.
// Reference: Building & Construction General On-site Award 2020
const DEFAULT_CLASSIFICATION_OT_THRESHOLDS: Record<string, number> = {
  ELECTRICAL_A_GRADE:       8.0,   // standard trade — 8h ordinary
  ELECTRICAL_APPRENTICE_1:  7.6,   // apprentices — 38h/5 = 7.6h per day
  ELECTRICAL_APPRENTICE_2:  7.6,
  ELECTRICAL_APPRENTICE_3:  7.6,
  ELECTRICAL_APPRENTICE_4:  7.6,
  TRAFFIC_CONTROLLER:       8.0,
  CIVIL_LABOURER:           8.0,
  SITE_MANAGER:             8.0,
  PROJECT_MANAGER:          8.0,   // salary — OT less common but tracked
  ADMINISTRATION:           7.6,   // office-based — 38h/5
}

let _classificationOTThresholds: Record<string, number> = { ...DEFAULT_CLASSIFICATION_OT_THRESHOLDS }

export function getOTThreshold(payClassification: string): number {
  return _classificationOTThresholds[payClassification] ?? 8.0
}

export function setClassificationOTThresholds(thresholds: Record<string, number>): void {
  _classificationOTThresholds = { ...thresholds }
}

export function getClassificationOTThresholds(): Record<string, number> {
  return { ..._classificationOTThresholds }
}

// ─── F3: Weekly hours cap ────────────────────────────────────────
// Construction EBAs typically cap weekly hours at 60-72h.
// Roster allocation grid checks this before allowing a new allocation.
let _weeklyHoursCap: number = 72   // configurable in Settings > Pay & Rates

export function getWeeklyHoursCap(): number { return _weeklyHoursCap }
export function setWeeklyHoursCap(cap: number): void { _weeklyHoursCap = Math.max(38, cap) }

/** Calculate total allocated hours for an employee in a given week */
export function getWeeklyAllocatedHours(
  employeeId: string,
  weekDates: string[],
  allocations: Array<{ employeeId: string; date: string; shiftType: string }>,
): number {
  const SHIFT_HRS: Record<string, number> = { DAY: 8, NIGHT: 9, HALF: 4 }
  return allocations
    .filter(a => a.employeeId === employeeId && weekDates.includes(a.date))
    .reduce((sum, a) => sum + (SHIFT_HRS[a.shiftType] ?? 8), 0)
}

// ─── R4: Roster change / audit log ───────────────────────────────
// Every allocation add, edit, or remove is logged for:
//   - Dispute resolution (employee questions about their schedule)
//   - WHS incident investigation (what was the roster at the time?)
//   - Payroll audit trail (proving hours were allocated and then changed)
export type RosterChangeAction = 'ADD' | 'REMOVE' | 'MODIFY' | 'STATUS_CHANGE' | 'PUSH' | 'ROLLBACK'

export interface RosterChangeEntry {
  id:             string
  action:         RosterChangeAction
  employeeId:     string
  employeeName:   string
  date:           string
  jobId:          string | null
  jobNumber:      string | null
  shiftType:      string
  changedByName:  string
  changedAt:      string
  previousState:  string | null   // JSON description of what was there before
  newState:       string | null   // JSON description of what replaced it
  reason:         string
  weekStarting:   string
}

const _rosterChangeLog: RosterChangeEntry[] = []

export function addRosterChangeEntry(entry: Omit<RosterChangeEntry, 'id' | 'changedAt'>): void {
  _rosterChangeLog.push({
    ...entry,
    id:        `rcl-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
    changedAt: new Date().toISOString(),
  })
}

export function getRosterChangeLog(weekStarting?: string): RosterChangeEntry[] {
  return weekStarting
    ? _rosterChangeLog.filter(e => e.weekStarting === weekStarting)
    : [..._rosterChangeLog]
}

export function clearRosterChangeLog(): void {
  _rosterChangeLog.splice(0)
}
